import { MarketingShell } from '@/components/marketing/warm/marketing-shell';
import { CtaBand } from '@/components/marketing/warm/cta-band';
import { AnswerCapsule } from '@/components/marketing/answer-capsule';

interface ContentPageProps {
  pill: string;
  title: string;
  subtitle: string;
  answer?: React.ReactNode;
  ctaTitle?: string;
  ctaSubtitle?: string;
  children: React.ReactNode;
}

/**
 * Shared shell for long-form content pages (guides, examples, alternatives,
 * best-of listicles): warm page, Sentient headline, single readable column,
 * shared closing CTA band.
 */
export function ContentPage({
  pill,
  title,
  subtitle,
  answer,
  ctaTitle = 'Try Kora free',
  ctaSubtitle = 'Pay as you go: $1 = 10 credits, 1 credit per ticket. Start with 1,100 free credits. No per-seat pricing.',
  children,
}: ContentPageProps) {
  return (
    <MarketingShell>
      <div className="docs-warm">
        <section className="pt-28 md:pt-36 pb-8">
          <div className="max-w-3xl mx-auto px-5 sm:px-8">
            <div className="hero-enter">
              <p className="warm-eyebrow mb-6">{pill}</p>
              <h1 className="font-display text-[2.5rem] md:text-[3.25rem] leading-[1.06] tracking-tight mb-6" style={{ color: 'var(--warm-text)' }}>
                {title}
              </h1>
              <p className="text-lg leading-relaxed mb-10" style={{ color: 'var(--warm-muted)' }}>{subtitle}</p>
            </div>
            {answer && <AnswerCapsule>{answer}</AnswerCapsule>}
          </div>
        </section>

        <div className="pb-16">
          <div className="max-w-3xl mx-auto px-5 sm:px-8">{children}</div>
        </div>

        <CtaBandWrap title={ctaTitle} lead={ctaSubtitle} />
      </div>
    </MarketingShell>
  );
}

function CtaBandWrap({ title, lead }: { title: string; lead: string }) {
  return (
    <div style={{ borderTop: '1px solid var(--warm-border)' }}>
      <CtaBand title={title} lead={lead} />
    </div>
  );
}

/** Section heading used inside content pages. */
export function ContentSection({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="mb-12">
      <h2 className="font-display text-2xl md:text-[1.75rem] leading-tight mb-4" style={{ color: 'var(--warm-text)' }}>
        {title}
      </h2>
      <div className="space-y-4 text-[15px] leading-[1.75]" style={{ color: 'var(--warm-muted)' }}>
        {children}
      </div>
    </section>
  );
}
